import { useCallback, useMemo } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useActionChoices } from "stores";
import { useUserContext } from "context/UserProvider";
import { useActionModalGetContext } from "context/ActionModalProvider";
import { ACTION_STATUS, OPERATIONS } from "constants/actions";
import { ACTION_VIEW_MODE, TASK_INPUT_NAMES } from "constants/action-form";
import { schemas } from "schemas";
import { useActionCalls, useTransformAction } from "./useActionsUtils";
import { constructActionPayload } from "./actionPayloadHandlers";

export const useActionFormikSetup = ({
  selectedFields,
  onClose,
  onActionSaved = () => {},
}) => {
  const { t } = useTranslation();
  const selectedFieldsIds = useMemo(
    () => selectedFields.map((field) => field.id),
    [selectedFields],
  );

  const { userData } = useUserContext();
  const { action, isBulk } = useActionModalGetContext();
  const { actions, getActionById, getOperationById } = useActionChoices();
  const { transformAction } = useTransformAction();
  const { postAction, postBulkAction, patchAction, isLoading } = useActionCalls(
    selectedFieldsIds?.[0],
  );

  const isNew = action.type === ACTION_VIEW_MODE.new;

  const initialFormValues = useMemo(() => {
    const baseValues = {
      ...(selectedFieldsIds.length === 1
        ? { fieldId: selectedFieldsIds[0] }
        : {
            fieldIds: selectedFieldsIds,
          }),
      ...(isNew
        ? {
            createdByUserId: userData.id,
            status: action.status,
            initialStatus: action.initialStatus,
            taskId: "",
          }
        : {}),
      ...(action.action
        ? { ...transformAction(action.action) }
        : {
            actionTypeId: 1,
            taskId: "",
            dateRange: {
              from: "",
              to: "",
            },
          }),
    };

    // camvio-action-plan suggested actions may carry a raw actionTechniqueId
    // hint on the task-specific action object. When the selected operation
    // has a catalog-driven typeVariants list (e.g. Plowing/Mulch), seed the
    // matching technique-variant field with it. Otherwise, leave
    // actionTechniqueId/actionTechniqueName on the action object as-is so
    // OtherOperationVariant can fall back to displaying/submitting them
    // directly for operations with no predefined options list.
    const taskInputName = TASK_INPUT_NAMES[getActionById(baseValues.taskId)];
    const taskActionValues = taskInputName
      ? baseValues[taskInputName]
      : undefined;

    if (!taskActionValues || !("actionTechniqueId" in taskActionValues)) {
      return baseValues;
    }

    const { actionTechniqueId } = taskActionValues;
    const operation = getOperationById(taskActionValues.operationTypeId, true);
    const { typeVariants, typeVariantFieldName } = operation || {};
    const isValidCatalogOption =
      typeVariantFieldName &&
      !taskActionValues[typeVariantFieldName] &&
      typeVariants?.some((option) => option.value === actionTechniqueId);

    if (!isValidCatalogOption) {
      return baseValues;
    }

    const { actionTechniqueId: _seededTechniqueId, ...taskActionRest } =
      taskActionValues;

    return {
      ...baseValues,
      [taskInputName]: {
        ...taskActionRest,
        [typeVariantFieldName]: actionTechniqueId,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFieldsIds,
    action,
    transformAction,
    getActionById,
    getOperationById,
  ]);

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: schemas.actions(t, actions),
    validateOnChange: true,
    onSubmit: ({
      shouldCheckEnemyId,
      stressType,
      status,
      initialStatus,
      ...values
    }) => {
      const taskId = values.taskId;
      const type = getActionById(taskId);
      const extraHelpers = {
        otherOperation: getOperationById(values?.otherAction?.operationTypeId),
        diagnoseOperation: getOperationById(
          values?.diagnoseAction?.operationTypeId,
        ),
      };
      const finalValues = constructActionPayload(type, values, extraHelpers);
      const handleMutationSuccess = () => {
        onActionSaved?.();
        onClose?.();
      };

      if (isBulk) {
        postBulkAction({
          status,
          values: finalValues,
          onSuccess: handleMutationSuccess,
          onError: onClose,
        });
        return;
      }
      if (isNew) {
        postAction({
          status,
          values: finalValues,
          onSuccess: handleMutationSuccess,
          onError: onClose,
        });
        return;
      }
      patchAction({
        status,
        initialStatus,
        values: finalValues,
        onSuccess: handleMutationSuccess,
        onError: onClose,
      });
    },
  });

  return { formik, isLoading };
};

export const useAnalysisFormikSetup = ({ selectedField, onClose, type }) => {
  const { t } = useTranslation();
  const { userData } = useUserContext();
  const { actions, getActionById, getOperationById } = useActionChoices();
  const { postAction, isLoading } = useActionCalls(selectedField?.id);

  const diagnoseTaskId = actions.diagnose.taskId;

  const initialTypeId =
    type === "leaf"
      ? actions.diagnose?.operations?.find(
          (op) => op.operationType === OPERATIONS.DIAGNOSE.LEAF_ANALYSIS,
        )?.operationTypeId
      : actions.diagnose?.operations?.find(
          (op) => op.operationType === OPERATIONS.DIAGNOSE.SOIL_SAMPLING,
        )?.operationTypeId;

  const initialValues = {
    actionTypeId: 1,
    fieldId: selectedField?.id,
    taskId: diagnoseTaskId,
    createdByUserId: userData.id,
    datetimePerformed: { from: moment(), to: moment() },
    dateRange: { from: "", to: "" },
    timeTaken: 10,
    diagnoseAction: {
      analysis: {},
      sampleCoordinates: [],
      operationTypeId: initialTypeId,
    },
  };

  const handleSubmit = useCallback(
    (values, helpers) => {
      const taskId = values.taskId;
      const type = getActionById(taskId);
      const extraHelpers = {
        diagnoseOperation: getOperationById(
          values?.diagnoseAction?.operationTypeId,
        ),
      };
      const finalValues = constructActionPayload(type, values, extraHelpers);

      postAction({
        status: ACTION_STATUS.performed,
        values: finalValues,
        onSuccess: () => {
          onClose?.();
          helpers.resetForm();
        },
      });
    },
    [getActionById, onClose, postAction, getOperationById],
  );

  const formik = useFormik({
    initialValues,
    validationSchema: schemas.analysis(t),
    onSubmit: handleSubmit,
  });

  return { formik, isLoading };
};
